import { useMemo } from "react";
import EntityMember from "_shared/entity-members/entity-member";
import EntityMemberSchema from "_shared/entity-members/entity-member-schema";
import type { ProposalTimelineEntry } from "work/proposals/negotiation/proposal-collaboration-contract";
import { getProposalCollectionItemIdentity } from "work/proposals/negotiation/proposal-operation-identity";
import {
  removedProposalCollectionItems,
  useRemovedProposalCollectionItems,
  type ProposalRemovedCollectionItem,
} from "work/proposals/negotiation/proposal-removed-collection-items";
import ProposalField from "work/proposals/proposal-fields/proposal-field";

/** The path the vendor team's members negotiate under. */
export const proposalTeamMembersPath = "/vendorTeam/members";

/**
 * The members a team no longer has, read out of the team's own history — see
 * `proposal-removed-collection-items.ts` for why a collection keeps its removed items as rows (#4954).
 * This is the team's reading of those items: a removal's expected value is the member as the stream
 * holds them, two ids that `EntityMember.fromJSON` reads; the name is the Individuals directory's.
 */
export type ProposalTeamRemovedMember = {
  /** The member's identity in the negotiation stream — the compound `userId:legalEntityId`. */
  itemId: string;
  /** The member as they stood when they were removed. */
  member: EntityMember;
  /** The removal's place in the stream, so the rows read in the order the members left. */
  sequence: number;
};

const NO_REMOVED_MEMBERS: readonly ProposalTeamRemovedMember[] = [];

/**
 * A member's identity in the negotiation stream, for saying which members a team still has. The same
 * compound identity `proposal-operation-identity.ts` mints for the collection, so a present member and
 * the stream's record of them can never disagree on casing or shape.
 */
export function proposalTeamMemberItemId(member: EntityMember | null | undefined): string | undefined {
  return member ? getProposalCollectionItemIdentity(member.toJSON()) : undefined;
}

/**
 * The members whose newest event in `entries` is a removal and whose identity is not in
 * `presentItemIds`, oldest removal first.
 */
export function removedProposalTeamMembers(
  entries: readonly ProposalTimelineEntry[],
  presentItemIds: Iterable<string>,
): ProposalTeamRemovedMember[] {
  return removedMembers(removedProposalCollectionItems(entries, proposalTeamMembersPath, presentItemIds));
}

function removedMembers(items: readonly ProposalRemovedCollectionItem[]): ProposalTeamRemovedMember[] {
  return items.flatMap((item) => {
    const parsed = EntityMemberSchema.EntityMemberJSONZ.safeParse(item.value);
    if (!parsed.success) return [];
    const member = EntityMember.fromJSON(parsed.data);
    return member ? [{ itemId: item.itemId, member, sequence: item.sequence }] : [];
  });
}

/** The removed members of the vendor team of `negotiationId`, for a roster that already lists the `presentItemIds`. */
export function useRemovedProposalTeamMembers(args: {
  negotiationId?: string;
  viewerScope?: string;
  presentItemIds: readonly string[];
}): readonly ProposalTeamRemovedMember[] {
  const items = useRemovedProposalCollectionItems({
    ...args,
    field: ProposalField.Team,
    collectionPath: proposalTeamMembersPath,
  });
  return useMemo(() => (items.length ? removedMembers(items) : NO_REMOVED_MEMBERS), [items]);
}
